import { doc, getDoc, updateDoc, increment, arrayUnion, arrayRemove } from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';

/**
 * ติดตามการดูรูปภาพของผู้ใช้
 * @param {string} userId - ID ของผู้ใช้
 * @param {string} girlId - ID ของน้อง
 * @param {string} imageId - ID ของรูปภาพ
 * @returns {Promise<object>} ผลลัพธ์การติดตาม
 */
export const trackImageView = async (userId, girlId, imageId) => {
  try {
    const userRef = doc(db, 'users', userId);
    const userSnap = await getDoc(userRef);
    
    if (!userSnap.exists()) {
      throw new Error('ไม่พบข้อมูลผู้ใช้');
    }

    const userData = userSnap.data();
    const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
    
    // ตรวจสอบการดูรูปในวันนี้
    const todayViews = userData.dailyImageViews || {};
    const todayViewCount = todayViews[today] || 0;
    
    // ตรวจสอบการดูรูปนี้แล้วหรือยัง
    const viewedImages = userData.viewedImages || [];
    const hasViewedThisImage = viewedImages.some(view => 
      view.imageId === imageId && view.date === today
    );

    return {
      todayViewCount,
      hasViewedThisImage,
      viewedImages: viewedImages.filter(view => view.date === today),
      userData
    };
  } catch (error) {
    console.error('Error tracking image view:', error);
    throw error;
  }
};

/**
 * บันทึกการดูรูปภาพ
 * @param {string} userId - ID ของผู้ใช้
 * @param {string} girlId - ID ของน้อง
 * @param {string} imageId - ID ของรูปภาพ
 * @param {object} imageData - ข้อมูลรูปภาพ
 * @returns {Promise<void>}
 */
export const recordImageView = async (userId, girlId, imageId, imageData) => {
  try {
    const userRef = doc(db, 'users', userId);
    const today = new Date().toISOString().split('T')[0];
    
    // เพิ่มการดูรูปในวันนี้
    await updateDoc(userRef, {
      [`dailyImageViews.${today}`]: increment(1),
      viewedImages: arrayUnion({
        imageId,
        girlId,
        date: today,
        timestamp: new Date().toISOString(),
        imageData: {
          url: imageData.url,
          thumbnail: imageData.thumbnail,
          originalName: imageData.originalName
        }
      })
    });

    // บันทึกสถิติการดูรูปของน้อง
    const girlRef = doc(db, 'girls', girlId);
    await updateDoc(girlRef, {
      [`imageViews.${imageId}.${today}`]: increment(1),
      [`imageViews.${imageId}.total`]: increment(1)
    });

    console.log(`✅ บันทึกการดูรูปภาพ: ${imageId} โดยผู้ใช้: ${userId}`);
  } catch (error) {
    console.error('Error recording image view:', error);
    throw error;
  }
};

/**
 * ตรวจสอบสิทธิ์การดูรูปภาพตาม subscription
 * @param {object} userData - ข้อมูลผู้ใช้
 * @param {number} currentViewCount - จำนวนการดูในวันนี้
 * @returns {object} สถานะสิทธิ์
 */
export const checkImageViewPermission = (userData, currentViewCount) => {
  const subscription = userData.subscription || 'free';
  const role = userData.role || 'customer';
  
  // กำหนดสิทธิ์ตาม subscription
  const viewLimits = {
    customer: {
      free: 3,
      basic: 10,
      silver: 25,
      gold: 50,
      platinum: 100
    },
    girl: {
      free: 5,
      basic: 15,
      premium: 30,
      vip: -1 // ไม่จำกัด
    },
    momo: {
      starter: 10,
      basic: 20,
      pro: 40,
      enterprise: 80,
      ultraEnterprise: -1 // ไม่จำกัด
    }
  };

  const dailyLimit = viewLimits[role]?.[subscription] || 0;
  
  if (dailyLimit === -1) {
    return {
      canView: true,
      remainingViews: -1,
      isUnlimited: true,
      needsUpgrade: false,
      needsPurchase: false
    };
  }

  const remainingViews = Math.max(0, dailyLimit - currentViewCount);
  const canView = remainingViews > 0;
  
  return {
    canView,
    remainingViews,
    dailyLimit,
    isUnlimited: false,
    needsUpgrade: !canView && subscription !== 'platinum' && role === 'customer',
    needsPurchase: !canView
  };
};

/**
 * สร้างข้อเสนออัพเกรด package
 * @param {object} userData - ข้อมูลผู้ใช้
 * @returns {object} ข้อเสนออัพเกรด
 */
export const generateUpgradeOffer = (userData) => {
  const subscription = userData.subscription || 'free';
  const role = userData.role || 'customer';
  
  const upgradeOptions = {
    customer: {
      free: { target: 'basic', price: 100, views: 10, discount: 0 },
      basic: { target: 'silver', price: 250, views: 25, discount: 10 },
      silver: { target: 'gold', price: 500, views: 50, discount: 15 },
      gold: { target: 'platinum', price: 1000, views: 100, discount: 20 }
    },
    girl: {
      free: { target: 'basic', price: 100, views: 15, discount: 0 },
      basic: { target: 'premium', price: 300, views: 30, discount: 10 },
      premium: { target: 'vip', price: 800, views: -1, discount: 20 }
    },
    momo: {
      starter: { target: 'basic', price: 200, views: 20, discount: 0 },
      basic: { target: 'pro', price: 500, views: 40, discount: 10 },
      pro: { target: 'enterprise', price: 1000, views: 80, discount: 15 },
      enterprise: { target: 'ultraEnterprise', price: 2000, views: -1, discount: 25 }
    }
  };

  const currentOption = upgradeOptions[role]?.[subscription];
  
  if (!currentOption) {
    return null; // ไม่มีอัพเกรดให้
  }

  return {
    ...currentOption,
    originalPrice: currentOption.price,
    discountedPrice: Math.round(currentOption.price * (1 - currentOption.discount / 100)),
    savings: currentOption.price - Math.round(currentOption.price * (1 - currentOption.discount / 100))
  };
};

/**
 * สร้างข้อเสนอซื้อบริการเสริม
 * @param {number} additionalViews - จำนวนการดูเพิ่มเติม
 * @returns {object} ข้อเสนอซื้อบริการเสริม
 */
export const generateAdditionalViewOffer = (additionalViews = 100) => {
  const basePrice = 499; // ราคาปกติ
  const offerPrice = 99; // ราคาพิเศษ
  
  return {
    additionalViews,
    originalPrice: basePrice,
    offerPrice,
    savings: basePrice - offerPrice,
    discount: Math.round(((basePrice - offerPrice) / basePrice) * 100),
    validUntil: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString() // 24 ชั่วโมง
  };
};

/**
 * ซื้อบริการเสริมการดูรูปภาพ
 * @param {string} userId - ID ของผู้ใช้
 * @param {number} additionalViews - จำนวนการดูเพิ่มเติม
 * @param {number} price - ราคาที่ต้องจ่าย
 * @returns {Promise<void>}
 */
export const purchaseAdditionalViews = async (userId, additionalViews, price) => {
  try {
    const userRef = doc(db, 'users', userId);
    const today = new Date().toISOString().split('T')[0];
    
    // เพิ่มสิทธิ์การดูรูปเพิ่มเติม
    await updateDoc(userRef, {
      [`additionalViews.${today}`]: increment(additionalViews),
      coins: increment(-price),
      purchaseHistory: arrayUnion({
        type: 'additionalViews',
        amount: additionalViews,
        price,
        date: today,
        timestamp: new Date().toISOString()
      })
    });

    console.log(`✅ ซื้อสิทธิ์การดูรูปเพิ่มเติม: ${additionalViews} ครั้ง ราคา: ${price} เหรียญ`);
  } catch (error) {
    console.error('Error purchasing additional views:', error);
    throw error;
  }
};









